import { Box, Copy, Flex, HorizontalRule, Image, themeColor } from '@elparking/components'
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import BookingDatesInfo from './BookingDatesInfo'
import { Ok } from '../Icons'
import { CarFeatures } from '../CarFeatures'
import { MUTUAMAS_SERVICES_CONDITIONS, RENTAL_CAR_CONTRACT_STATUS as STATUS } from 'commons/js/constants'
import CancelButton from '../CancelButton'
import EditBookingButton from '../EditBookingButton'
import IconRounded from 'commons/js/ui/IconRounded'

const BookingVehicleSummaryContainer = styled(Flex)`
    border: 1px solid ${themeColor('lightGray40')};
`
const CancelBlock = ({ id, onSuccess, token, pageName }) => <Flex alignItems={['start', 'center']} flexDirection={['column', 'row']}>
    <CancelButton token={token} onSuccess={onSuccess} id={id} pageName={pageName} />
</Flex>

CancelBlock.propTypes = {
    id: PropTypes.any.isRequired,
    onSuccess: PropTypes.func.isRequired,
    token: PropTypes.string,
    pageName: PropTypes.string,
}

const EditBookingBlock = ({ id, onSuccess, token, pageName }) => <Flex alignItems={['start', 'center']} flexDirection={['column', 'row']}>
    <EditBookingButton token={token} onSuccess={onSuccess} id={id} pageName={pageName}/>
</Flex>

EditBookingBlock.propTypes = {
    id: PropTypes.any.isRequired,
    onSuccess: PropTypes.func.isRequired,
    token: PropTypes.string,
    pageName: PropTypes.string,
}

export const Summary = ({
    id,
    supplierReference,
    startRent,
    endRent,
    features,
    car,
    seller,
    cancelled,
    isCancelable,
    cancelBlock: CancelBlock,
    editBookingBlock: EditBookingBlock,
    token,
    goToRentalCarLanding,
    timeZone,
    pageName,
}) => {
    const [ isCancelled, setCancelled ] = useState(cancelled)
    const onEditBooking = () => {
        setCancelled(true)
        goToRentalCarLanding()
    }
    return <>
        {isCancelled && <Box background='warning20' padding='12px' margin='1em 0em'>
            <IconRounded type='warning-line' background='warning_support' fontSize='medium' color='warning_dark' /> <Copy as='span' fontWeight='book' color='gray' padding='0'>
                {isCancelled !== STATUS.RENTAL_CAR_CONTRACT_CANCELLED_BY_USER
                    ? <FormattedMessage defaultMessage='Tu reserva ha sido cancelada sin cargo. Si necesitas ayuda contacta con nuestro servicio de atención al cliente.' />
                    : <FormattedMessage defaultMessage='Reserva cancelada' />
                }
            </Copy>
        </Box>}
        <BookingVehicleSummaryContainer flexDirection='column' width='100%' padding='16px' margin='1em 0em'>
            <Box width='100%'>
                <Copy fontSize='medium' padding='0'>
                    <FormattedMessage defaultMessage='Nº de reserva' />
                    <Copy as='span' color='accent' padding='0 0 0 8px' fontSize='medium'>{supplierReference}</Copy>
                </Copy>
            </Box>
            <HorizontalRule/>
            <BookingDatesInfo
              startRent={startRent}
              endRent={endRent}
              timeZone={timeZone}/>
            <HorizontalRule/>
            <Flex flexDirection={['column', 'row']} alignItems='start'>
                <Flex alignItems='center' pb={['24px', '0px']} width={['100%', '440px']}>
                    {car && <Image
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null
                        currentTarget.src = '/images/rental-car/default_car.webp'
                    }}
                      width='110px'
                      height='75px'
                      alt={seller.name}
                      src={car.image} />}
                    <Flex flexDirection='column' width='auto' flexWrap='wrap' padding='0px 16px 0px 16px'>
                        <Copy fontWeight='bold' fontSize='medium' padding='0px 0px 5px 0px'>{car.description}</Copy>
                        <Copy fontWeight='regular' fontSize='small' padding='0px 0px 5px 0px'>{car.type}</Copy>
                        <Image src={seller.image} width='81px' alt={seller.name} />
                    </Flex>
                </Flex>
                <Flex flexDirection='column' >
                    <CarFeatures {...car.features} iconColor='accent' />
                    {features && <Flex flexWrap='wrap' padding='8px 0px 0px 0px'>{ features.map((extra) => <Flex key={`feature_${extra}`} padding={['0px 16px 8px 0px', '0px 16px 4px 0px']}>
                        <Ok color='accent'/>
                        <Copy fontSize='small' fontWeight='regular' padding='0px 0px 0px 8px'>{extra}</Copy>
                    </Flex>)}
                    </Flex>}
                </Flex>
            </Flex>
            <HorizontalRule/>
            {(!isCancelled && isCancelable) && <>
                <Flex flexDirection={['column', 'row']}>
                    <EditBookingBlock token={token} onSuccess={onEditBooking} id={id} pageName={pageName} />
                    <CancelBlock token={token} onSuccess={() => setCancelled(true)} id={id} pageName={pageName} />
                </Flex>
                <Copy as='span' fontSize='small' fontWeight='regular'><FormattedMessage defaultMessage='Cancela en cualquier momento y sin coste adicional. Consulta las {conditionsLink}.' values={{
                    conditionsLink: <Copy as='a' href={MUTUAMAS_SERVICES_CONDITIONS} fontSize='small' rel='noopener noreferrer' target='_blank' textDecoration='underline'><FormattedMessage defaultMessage='condiciones de alquiler'/></Copy>,
                }}/></Copy>
            </>}
        </BookingVehicleSummaryContainer>
    </>
}

Summary.defaultProps = {
    cancelBlock: CancelBlock,
    editBookingBlock: EditBookingBlock,
    cancelled: false,
}

Summary.propTypes = {
    id: PropTypes.any,
    cancelled: PropTypes.bool,
    supplierReference: PropTypes.any,
    seller: PropTypes.shape({
        name: PropTypes.string,
        image: PropTypes.string,
    }),
    car: PropTypes.shape({
        brand: PropTypes.string,
        model: PropTypes.string,
        type: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.string,
        features: PropTypes.shape({
            oilType: PropTypes.string,
            gear: PropTypes.string,
            doors: PropTypes.number,
            seats: PropTypes.number,
            suitcase: PropTypes.number,
        }),
    }),
    features: PropTypes.arrayOf(PropTypes.string),
    startRent: PropTypes.object,
    endRent: PropTypes.object,
    onCancel: PropTypes.func,
    isCancelable: PropTypes.bool,
    cancelBlock: PropTypes.func,
    editBookingBlock: PropTypes.func,
    token: PropTypes.string,
    goToRentalCarLanding: PropTypes.func,
    timeZone: PropTypes.string,
    pageName: PropTypes.string,
}

export default Summary
